import { postWithAuth } from "@/lib/api-helpers";
import { ObjectId } from "bson";
import { Block } from "@/types/block";
import { createBlockMutation } from "@/indexedDB";

export interface CreateTemplateResponse {
    isError?: boolean;
    message?: string;
    [key: string]: any;
}

/**
 * Creates a new template page atomically through the offline-sync architecture.
 *
 * Writes the template block to IndexedDB (`appDB.blocks`, isSync: 0) and enqueues a
 * BLOCK_CREATE op (`appDB.syncQueue`, isSync: 0) in a single transaction. SyncWatcher
 * picks the op up and POSTs to /api/note/block/batch-create with `isTemplate: true`
 * at the top level — the backend reads it from there
 * (note.controller.ts:37 → BlockService.batchCreateBlocks).
 *
 * The caller is responsible for:
 *   - Optimistic context updates (addBlock).
 *   - Updating the IDB sidebar order via addTemplatePageMutation so the sidebar's
 *     useLiveBlocks query renders the new template instantly.
 *
 * If IDB is unavailable, createBlockMutation falls through to a direct API call
 * (see blockMutations.ts:735) — same behavior as regular page creation.
 */
export async function createTemplateBlocks({
    workspaceId,
    userEmail,
    title = "New page template",
    icon = "",
}: {
    workspaceId: string;
    userEmail: string;
    title?: string;
    icon?: string;
}) {
    const newTemplateId = new ObjectId().toString();

    const templateBlock: Block = {
        _id: newTemplateId,
        blockType: "page",
        workspaceId: workspaceId,
        workareaId: null,
        parentId: workspaceId,
        parentType: "workspace",
        value: {
            title,
            userId: userEmail,
            userEmail: userEmail,
            icon,
            coverURL: null,
            pageType: "template",
            isTemplate: true,
        },
        blockIds: [],
        status: "alive",
    };

    // Atomic IDB write + queue BLOCK_CREATE for SyncWatcher.
    // The workspace parent is not stored in appDB.blocks, so the parent-blockIds
    // update step inside createBlockMutation is automatically a no-op
    // (see blockMutations.ts:760-771).
    await createBlockMutation({
        block: templateBlock,
        workspaceId,
        workareaId: null,
        parentId: workspaceId,
        parentTable: "workspace",
        insertAfterBlockID: null,
        isTemplate: true,
    });

    return {
        templateId: newTemplateId,
        templateBlock,
    };
}

export type TemplateTarget = "private" | "public" | "restricted" | "workarea";

// Mirror of the backend `ParentTable` union. `targetType` covers visibility
// (private/public/restricted/workarea root); `TemplateParentType` covers the
// immediate parent's table when the new root is nested under a page,
// generic block, collection_view, or a datasource row.
export type TemplateParentType =
    | "workspace"
    | "workarea"
    | "page"
    | "block"
    | "collection_view"
    | "collection";

export async function instantiateTemplateBlocks({
    templateBlockId,
    targetParentId,
    workspaceId,
    targetType,
    workareaId,
    targetParentType,
}: {
    templateBlockId: string;
    targetParentId: string;
    workspaceId: string;
    targetType: TemplateTarget;
    workareaId?: string;
    targetParentType?: TemplateParentType;
}) {
    const response = await postWithAuth("/api/template/instantiate", {
        templateBlockId,
        targetParentId,
        workspaceId,
        targetType,
        ...(workareaId && { workareaId }),
        ...(targetParentType && { targetParentType }),
    });

    if (response && typeof response === "object" && "isError" in response && response.isError) {
        throw new Error(response.message || "Failed to instantiate template");
    }

    return response;
}

/**
 * Copy an APPROVED marketplace template into the caller's workspace.
 *
 * Unlike instantiateTemplateBlocks (which clones an arbitrary block id), this
 * targets a marketplace listing: the server resolves the workspace-free global
 * clone (globalTemplateBlockId) and copies it into the target workspace,
 * attaching the real workspaceId there and bumping the copy count. This is the
 * storefront "use this template" action.
 */
export async function copyTemplateToWorkspace({
    templateMarketplaceId,
    targetWorkspaceId,
    targetType = "private",
    targetParentId,
    workareaId,
    targetParentType,
}: {
    templateMarketplaceId: string;
    targetWorkspaceId: string;
    targetType?: TemplateTarget;
    targetParentId?: string;
    workareaId?: string;
    targetParentType?: TemplateParentType;
}) {
    const response = await postWithAuth("/api/template/copy-to-workspace", {
        templateMarketplaceId,
        targetWorkspaceId,
        targetType,
        ...(targetParentId && { targetParentId }),
        ...(workareaId && { workareaId }),
        ...(targetParentType && { targetParentType }),
    });

    if (response && typeof response === "object" && "isError" in response && response.isError) {
        throw new Error((response as { message?: string }).message || "Failed to copy template");
    }

    return response;
}
